import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useMemo, useState } from "react";

import { useAuth } from "~/contexts/AuthContext";
import { EmploymentTypeId } from "~/modules/humanResources/api/employmentType/employmentTypeTypes.ts";
import { useAllLocations, useUsersAvailableLocations } from "~/modules/location/api/location/locationQueries.ts";
import { useUsersActiveStaffings } from "~/modules/project/api/staffing/staffingQueries.ts";
import type { UsersActiveStaffingType } from "~/modules/project/api/staffing/staffingTypes.ts";
import { useUsersAvailableDeliverables } from "~/modules/timeTracking/api/deliverable/deliverableQueries.ts";
import { monthlyClosingQueries } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingQueries.ts";
import { useUsersTimeTrackings } from "~/modules/timeTracking/api/timeTracking/timeTrackingQueries.ts";
import { useAllTimeTrackingTypes } from "~/modules/timeTracking/api/timeTrackingType/timeTrackingTypeQueries.ts";
import {
	getStartAndEndOfMonthFormatted,
	getTrackedStaffingIdsOnDay,
	isDateInsideStaffingPeriod,
	removeStaffingsFromProjects,
} from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";

const noActiveStaffings: UsersActiveStaffingType[] = [];

type UseTimeTrackingActionsParams = {
	userId: User["id"];
	/** `YYYY-MM`, or empty while the month is not known yet. */
	month: string;
	/** A month change keeps the old trackings and closing until the new ones arrive. See `isChangingMonth`. */
	keepPreviousMonth?: boolean;
};

/**
 * The time tracking actions of one user in one month: the queries, the restrictions, and the state of the create
 * sidebar, the update sidebar and the delete dialog. The time tracking page and the dashboard calendar both use this
 * controller, because the calendar is a second view of the same feature: both views must keep the same restrictions.
 *
 * `queries` holds the queries that a view waits for before it renders; the view puts them into its loading gate.
 */
export function useTimeTrackingActions({ userId, month, keepPreviousMonth = false }: UseTimeTrackingActionsParams) {
	const { user } = useAuth();
	// The logged-in user, not the tracked user.
	const userIsFreelancer = user!.employmentTypeId === EmploymentTypeId.Freelancer;

	const queries = {
		activeStaffingsAndQuotas: useUsersActiveStaffings(userId, month),
		locations: useAllLocations(),
		timeTrackingTypes: useAllTimeTrackingTypes(),
		users: useAllUsers(),
		userDeliverables: useUsersAvailableDeliverables(userId),
		userLocations: useUsersAvailableLocations(userId),
	};

	const filter = useMemo(() => {
		return month ? getStartAndEndOfMonthFormatted(parseISO(`${month}-01`)) : { startDate: "", endDate: "" };
	}, [month]);

	const {
		isLoading: timeTrackingsAreLoading,
		isPlaceholderData: timeTrackingsArePlaceholder,
		data: timeTrackings,
	} = useUsersTimeTrackings({
		userId,
		filter,
		enabled: !!month,
		keepPreviousFilter: keepPreviousMonth,
	});

	const { isLoading: closingIsLoading, isPlaceholderData: closingIsPlaceholder, data: monthlyClosing } = useQuery({
		...monthlyClosingQueries.month(userId, month),
		...(keepPreviousMonth && { placeholderData: keepPreviousData }),
	});
	// A placeholder query has `status: "success"`, so `isLoading` stays false while the old month shows.
	const isChangingMonth = timeTrackingsArePlaceholder || closingIsPlaceholder;

	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [timeTrackingIdToUpdate, setTimeTrackingIdToUpdate] = useState<string | null>(null);
	const [timeTrackingIdToDelete, setTimeTrackingIdToDelete] = useState<string | null>(null);

	const activeStaffings = queries.activeStaffingsAndQuotas.data?.projects ?? noActiveStaffings;

	// The create form offers no staffing that is closed in this month. A Draft unlock keeps a closed staffing
	// editable, so an unlocked staffing stays in the form.
	const openActiveStaffings = useMemo(() => {
		const closedStaffingIds = monthlyClosing?.staffings
			.filter((staffing) => staffing.isClosed && staffing.unlocks.length === 0)
			.map((staffing) => staffing.staffingId) ?? [];
		return removeStaffingsFromProjects(activeStaffings, closedStaffingIds);
	}, [activeStaffings, monthlyClosing]);
	const nonProjectIsClosed = !!monthlyClosing?.nonProject.isClosed;
	// Not `!isMonthClosed`: the month can be not closed while every listed part is closed. A freelancer has
	// no non-project type, and an inactive staffing of the list takes no new tracking.
	const canAddTracking = openActiveStaffings.length > 0 || (!nonProjectIsClosed && !userIsFreelancer);
	// An unlock keeps the month closed, so `hasMonthClosing` without `isMonthClosed` is not the only sign.
	const hasClosingExceptionsOrUnlocks = !!monthlyClosing && (
		(monthlyClosing.hasMonthClosing && !monthlyClosing.isMonthClosed)
		|| monthlyClosing.staffings.some((staffing) => staffing.unlocks.length > 0)
	);
	// Only a month with closing exceptions or unlocks decides by day: a closed staffing with unlocks takes a
	// tracking only on a day inside one of its unlock periods. Every other month decides for all days at once.
	const canAddTrackingOn = useCallback((date: Date) => {
		if (!hasClosingExceptionsOrUnlocks) return canAddTracking;
		if (!nonProjectIsClosed && !userIsFreelancer) return true;
		const day = formatDateToYYYYMMDD(date);
		const unlocksByStaffingId = new Map(monthlyClosing?.staffings
			.filter((staffing) => staffing.unlocks.length > 0)
			.map((staffing) => [staffing.staffingId, staffing.unlocks]));
		return openActiveStaffings
			.flatMap((project) => project.projectPhases)
			.flatMap((phase) => phase.orders)
			.flatMap((order) => order.staffings)
			.some((staffing) => {
				if (!isDateInsideStaffingPeriod(date, staffing)) return false;
				const unlocks = unlocksByStaffingId.get(staffing.id);
				return !unlocks || unlocks.some((unlock) => unlock.deliveryStartDate <= day && day <= unlock.deliveryEndDate);
			});
	}, [canAddTracking, hasClosingExceptionsOrUnlocks, monthlyClosing, nonProjectIsClosed, openActiveStaffings, userIsFreelancer]);

	const timeTrackingToUpdate = useMemo(() => {
		if (timeTrackingIdToUpdate && timeTrackings) {
			return timeTrackings.find((timeTracking) => timeTracking.id === timeTrackingIdToUpdate) || null;
		}
		return null;
	}, [timeTrackingIdToUpdate, timeTrackings]);

	const timeTrackingToDelete = useMemo(() => {
		if (timeTrackingIdToDelete && timeTrackings) {
			return timeTrackings.find((timeTracking) => timeTracking.id === timeTrackingIdToDelete) || null;
		}
		return null;
	}, [timeTrackingIdToDelete, timeTrackings]);

	const trackedStaffingIdsOnSelectedDay = useMemo(() => {
		return getTrackedStaffingIdsOnDay({ date: selectedDate, timeTrackings: timeTrackings ?? [] });
	}, [selectedDate, timeTrackings]);

	// The tracking in the edit leaves out its own staffing, but keeps other trackings on the same staffing.
	const trackedStaffingIdsOnUpdateDay = useMemo(() => {
		return getTrackedStaffingIdsOnDay({
			date: timeTrackingToUpdate?.date ? new Date(timeTrackingToUpdate.date) : null,
			exceptTimeTrackingId: timeTrackingToUpdate?.id,
			timeTrackings: timeTrackings ?? [],
		});
	}, [timeTrackingToUpdate, timeTrackings]);

	const openCreate = useCallback((date: Date) => setSelectedDate(date), []);
	const openUpdate = useCallback((timeTrackingId: string) => setTimeTrackingIdToUpdate(timeTrackingId), []);
	const openDelete = useCallback((timeTrackingId: string) => setTimeTrackingIdToDelete(timeTrackingId), []);
	const closeCreate = useCallback(() => setSelectedDate(null), []);
	const closeUpdate = useCallback(() => setTimeTrackingIdToUpdate(null), []);
	const closeDelete = useCallback(() => setTimeTrackingIdToDelete(null), []);

	return {
		userId,
		month,
		queries,
		timeTrackings,
		timeTrackingsAreLoading,
		monthlyClosing,
		closingIsLoading,
		isChangingMonth,
		userIsFreelancer,
		activeStaffings,
		openActiveStaffings,
		nonProjectIsClosed,
		hasClosingExceptionsOrUnlocks,
		canAddTrackingOn,
		selectedDate,
		timeTrackingToUpdate,
		timeTrackingToDelete,
		trackedStaffingIdsOnSelectedDay,
		trackedStaffingIdsOnUpdateDay,
		openCreate,
		openUpdate,
		openDelete,
		closeCreate,
		closeUpdate,
		closeDelete,
	};
}

export type TimeTrackingController = ReturnType<typeof useTimeTrackingActions>;
